import { createHOC } from 'vue-hoc';
import isEqual from 'lodash/isEqual';
import gql from 'graphql-tag';
import Promise from 'bluebird';
import withUser from '~lib/common/hoc/withUser';

import auth from '~lib/common/providers/auth';
import userManagement from '~lib/common/providers/userManagement';
import casesService from '~lib/common/providers/casesService';

import CaseCopyTool from './CaseCopyTool';
import removeUncomparableCaseFields from '../CaseDiffTool/removeUncomparableCaseFields';
import parseGroupMetadata from '../../../utils/parseGroupMetadata';
import createUsersService from './usersService';

const options = {
  name: 'CaseCopyToolContainer',
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      state: {
        loading: false,
        copying: false,
        fromEnv: 'dev',
        toEnv: 'dev',
        error: null,
      },
    };
  },
  async created() {
    if (!(await auth.isAuthenticated())) {
      this.$router.replace({ name: 'login' });
      return;
    }
  },
  methods: {
  },
};

const renderWith = {
  props: {
    loading() {
      return this.state.loading;
    },
    copying() {
      return this.state.copying;
    },
    error() {
      return this.state.error;
    },
  },
  listeners: {
    updateFromEnv(env) {
      this.state.fromEnv = env;
    },
    updateToEnv(env) {
      this.state.toEnv = env;
    },
    async copy(selectedCaseIds, fromOrg, toOrg, fromUser, toUser, ignoreDiffs = false) {
      const casesCopiedSoFar = [];
      const { fromEnv, toEnv } = this.state;
      try {
        this.state.copying = true;
        this.state.error = null;
        const restoredCaseIds = await Promise.map(
          selectedCaseIds,
          async (selectedCaseId) => {
            const dumpResponse = await casesService.mutate({
              mutation: gql`
                mutation dumpCase($caseId: String) {
                  dumpCase(caseId: $caseId)
                }
              `,
              variables: {
                caseId: selectedCaseId,
              },
              context: {
                uri: `https://${fromEnv}.vrpatients.com/graphql`,
              },
            });
            const caseDump = dumpResponse.data.dumpCase;
            const toMetadata = parseGroupMetadata(toOrg);
            const toOrgAccess = {
              run: { users: [toUser.user_id] },
              read: { users: [toUser.user_id] },
              write: { users: [toUser.user_id] },
              share: { users: [toUser.user_id] },
              owner: toUser.user_id,
              ownerOrg: toOrg._id,
              productType: toMetadata.productType || 'EMS',
            };
            if (!caseDump.newCase) {
              return;
            }
            caseDump.newCase.access = toOrgAccess;
            // If the target environment does not have the new avatarKey field
            // read it from the old caseConfigurations_avatar relation
            if (!caseDump.newCase.avatarKey) {
              const avatarRelation = caseDump.relationMap.caseConfigurations_avatar[0];
              caseDump.newCase.avatarKey = avatarRelation ? avatarRelation._to.split('/')[1] : undefined;
            }
            // Uncomment this to emulate case data diff after copy
            // caseDump.newCase.name = 'TEST - Wrong case name after copy';
            const restoreResponse = await casesService.mutate({
              mutation: gql`
                mutation restoreCase($caseDump: JSON) {
                  restoreCase(caseDump: $caseDump)
                }
              `,
              variables: {
                caseDump,
              },
              context: {
                uri: `https://${toEnv}.vrpatients.com/graphql`
              }
            });
            const restoredCaseId = restoreResponse.data.restoreCase;
            casesCopiedSoFar.push(restoredCaseId);
            return restoredCaseId;
          },
          {
            // Throttle the copy a bit to prevent payload too large errors
            concurrency: 3,
          }
        );

        const fromUserLoginAsResponse = await userManagement.post('/auth/loginAs', {
          orgId: fromOrg._id,
          email: fromUser.email,
        });
        const fromUserToken = fromUserLoginAsResponse.data.idToken;

        const toUsersService = createUsersService(`https://${toEnv}.vrpatients.com/api/users`);
        const toUserLoginAsResponse = await toUsersService.post('/auth/loginAs', {
          orgId: toOrg._id,
          email: toUser.email,
        });
        const toUserToken = toUserLoginAsResponse.data.idToken;

        await Promise.map(restoredCaseIds, async (restoredCaseId, index) => {
          const dumpedCaseId = selectedCaseIds[index];
          const [dumpedCaseJson, restoredCaseJson] = await Promise.all([
            fetch(
              `https://${fromEnv}.vrpatients.com/api/cases/${dumpedCaseId}?code=obuicX9hPVJ1NlQyepu9lg==`,
              {
                headers: {
                  Authorization: `Bearer ${fromUserToken}`,
                },
              }
            ).then(async response => {
              if (response.ok) {
                return response.json();
              }
              const data = await response.json();
              throw new Error(`Error when fetching case in source environment: ${data && data.errorInfo && data.errorInfo.error || 'unknown error'}`)
            }),
            fetch(
              `https://${toEnv}.vrpatients.com/api/cases/${restoredCaseId}?code=obuicX9hPVJ1NlQyepu9lg==`,
              {
                headers: {
                  Authorization: `Bearer ${toUserToken}`,
                },
              }
            ).then(async response => {
              if (response.ok) {
                return response.json();
              }
              const data = await response.json();
              throw new Error(`Error when fetching case in target environment: ${data && data.errorInfo && data.errorInfo.error || 'unknown error'}`)
            }),
          ]);

          const comparableDumpedCaseJson = removeUncomparableCaseFields(
            dumpedCaseJson
          );
          const comparableRestoredCaseJson = removeUncomparableCaseFields(
            restoredCaseJson
          );
          if (!ignoreDiffs && !isEqual(comparableDumpedCaseJson, comparableRestoredCaseJson)) {
            const error = new Error(
              'Case data is not the same after copy. Please take a screenshot of the diff below and contact support.'
            );
            error.code = 'CASE_JSON_NOT_EQUAL';
            error.dumpedCaseJson = JSON.stringify(
              comparableDumpedCaseJson,
              null,
              2
            );
            error.restoredCaseJson = JSON.stringify(
              comparableRestoredCaseJson,
              null,
              2
            );
            throw error;
          }
        });
      } catch (e) {
        console.error(e);
        if (e.graphqlErrors && e.graphqlErrors.length) {
          this.state.error = e.graphqlErrors[0];
        }
        this.state.error = e;
        await casesService.mutate({
          mutation: gql`
            mutation bulkRemoveCaseConfigurations($caseIds: [ID]) {
              bulkRemoveCaseConfigurations(ids: $caseIds)
            }
          `,
          variables: {
            caseIds: casesCopiedSoFar,
          },
          context: {
            uri: `https://${toEnv}.vrpatients.com/graphql`
          }
        });
      } finally {
        this.state.copying = false;
      }
    },
  },
};

const CaseCopyToolContainer = createHOC(CaseCopyTool, options, renderWith);
export default withUser()(CaseCopyToolContainer);
